"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

import type { FilterIn } from "@/lib/api/model";

/**
 * The list's filter state lives in the `f` search param as `FilterIn[]` JSON
 * so the table (reader) and the toolbar's `FilterButton` (writer) stay
 * decoupled while sharing one source of truth.
 */
export function useListFilterParam() {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  const filters = useMemo<FilterIn[]>(() => {
    try {
      const parsed = JSON.parse(sp.get("f") ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [sp]);

  const setFilters = useCallback(
    (next: FilterIn[]) => {
      const p = new URLSearchParams(sp);
      if (next.length === 0) p.delete("f");
      else p.set("f", JSON.stringify(next));
      const qs = p.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    },
    [pathname, router, sp],
  );

  return { filters, setFilters };
}
